import { Clickable } from '@carvana/merch-components';
import { NavPayloadAction } from '@carvana/merch-native';
import cx from 'classnames';
import React, { MouseEventHandler, useCallback, useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import DefaultSuggestions from './DefaultSuggestions/DefaultSuggestions';
import NoResults from './NoResults';
import {
  SearchBarInnerWrapper,
  SearchBarWrapper,
  SearchButton,
  SuggestionsContainerClasses
} from './SearchBar.styles';
import SearchBarData from './SearchBarData';
import SearchBarInput from './SearchBarInput';
import Suggestions from './Suggestions';
import { SearchBarEvents } from '../constants';
import { type SearchBarProps } from '../context/constants';
import { InputContextProvider, useInputContext } from '../context/InputContext';
import useGetSuggestionHref from '../hooks/useGetSuggestionHref';
import useSearchBar from '../hooks/useSearchBar';
import CloseIcon from '../icons/LargeClose';
import SearchIcon from '../icons/Search';
import {
  SearchBarContextProvider,
  useSearchBarContext
} from '../SearchBarContext';
import { SuggestionsContextProvider } from '../SuggestionsContext';

function SearchBar() {
  const { noResults } = useInputContext();
  const {
    buttonSize = 'large',
    inputBgColor = 'white',
    onFocus,
    rounded,
    showButton = true
  } = useSearchBarContext();
  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    handleClearText,
    handleDefaultSuggestionClicked,
    handleGoButtonClick,
    handleInputOnChange,
    handleInputOnFocus,
    handleKeyDown,
    handleRemoveRecentSearch,
    handleSuggestionClick,
    hasSuggestionQuery,
    highlightedIndex,
    isOpen,
    setHighlightedIndex,
    suggestionResults
  } = useSearchBar();

  useEffect(() => {
    if (onFocus) {
      onFocus(isOpen);
    }
  }, [isOpen, onFocus]);

  const roundedClass = rounded ? `rounded-${rounded}` : 'rounded';
  const roundedTopClass = rounded ? `rounded-t-${rounded}` : 'rounded-t';

  return (
    <div className="h-full w-full" data-qa="search-bar-container">
      <form className="relative z-[1] h-full w-full" action=".">
        <SearchBarWrapper
          className={cx(
            'box-border h-full border border-grey-2 hover:bg-white',
            inputBgColor === 'gray' && !isOpen ? 'bg-grey-1' : 'bg-white',
            isOpen || (isOpen && noResults) ? roundedTopClass : roundedClass
          )}
        >
          <SearchBarInput
            getInputProps={getInputProps}
            handleClearText={handleClearText}
            handleInputOnChange={handleInputOnChange}
            handleInputOnFocus={handleInputOnFocus}
            handleKeyDown={handleKeyDown}
          />
          {showButton ? (
            <div className="p-[6px]">
              <SearchButton
                type="button"
                className={buttonSize === 'large' ? 'h-44 w-48' : undefined}
                onClick={handleGoButtonClick}
                data-analytics-id="Search Bar - Go"
              >
                GO
              </SearchButton>
            </div>
          ) : null}
        </SearchBarWrapper>

        <div
          className={cx(roundedClass, SuggestionsContainerClasses, {
            'rounded-t-none border border-t-0 border-grey-2': isOpen
          })}
        >
          <ul {...getMenuProps()} className="m-0 p-0">
            {isOpen ? (
              <Scrollbars
                autoHeight
                autoHeightMax={530}
                autoHeightMin={suggestionResults.length ? 0 : 530}
                hideTracksWhenNotNeeded
                universal
                renderThumbVertical={props => (
                  <div
                    {...props}
                    className="pointer relative block w-[4px] rounded-[6px] bg-grey-3"
                  />
                )}
                renderTrackVertical={props => (
                  <div
                    {...props}
                    className="absolute right-[8px] top-[20px] h-[calc(100%-40px)] rounded-[6px] bg-grey-2"
                    style={{ width: '4px' }}
                  />
                )}
              >
                <div className="flex flex-col gap-24 px-16 py-24">
                  {noResults ? (
                    <NoResults />
                  ) : (
                    <Suggestions
                      handleRemoveRecentSearch={handleRemoveRecentSearch}
                      handleSuggestionClick={handleSuggestionClick}
                      hasInput={hasSuggestionQuery}
                      highlightedIndex={highlightedIndex}
                      getItemProps={getItemProps}
                      setHighlightedIndex={setHighlightedIndex}
                      suggestionResults={suggestionResults}
                    />
                  )}
                  {hasSuggestionQuery && suggestionResults.length ? null : (
                    <DefaultSuggestions
                      handleDefaultSuggestionClicked={
                        handleDefaultSuggestionClicked
                      }
                    />
                  )}
                </div>
              </Scrollbars>
            ) : null}
          </ul>
        </div>
      </form>
    </div>
  );
}

const SearchBarModalButton = () => {
  const { inputValue, setInputValue } = useInputContext();
  const {
    buttonSize,
    inputBgColor = 'white',
    onShowModalClick,
    rounded,
    showButton = true
  } = useSearchBarContext();
  const getSuggestionHref = useGetSuggestionHref();

  const handleShowModalClick = useCallback(() => {
    onShowModalClick?.();
  }, [onShowModalClick]);

  const handleClearClick: MouseEventHandler<HTMLElement> = useCallback(
    e => {
      e.stopPropagation();
      onShowModalClick?.();
      setInputValue('');
    },
    [onShowModalClick, setInputValue]
  );

  return (
    <div
      className={cx(
        'relative flex h-full w-full items-center border border-grey-2 bg-white p-0',
        inputBgColor === 'gray' ? 'bg-grey-1' : 'bg-white',
        rounded ? `rounded-${rounded}` : 'rounded'
      )}
    >
      <Clickable
        data-analytics-id="Search Bar - Modal Open"
        data-qa="Search Bar - Modal Open"
        element={SearchBarInnerWrapper}
        onClick={handleShowModalClick}
      >
        <div className="flex pl-12 pr-8">
          <SearchIcon />
        </div>
        <input
          className="h-full w-full flex-1 border-none bg-transparent outline-none"
          placeholder="Search Make, Model, or Keyword"
          readOnly
          value={inputValue}
        />
        {inputValue && (
          <button
            aria-label="clear text"
            className="flex items-center bg-transparent p-16 text-grey-3"
            data-analytics-id={SearchBarEvents.ClearedText}
            onClick={handleClearClick}
            title="Clear Text"
            type="button"
          >
            <CloseIcon />
          </button>
        )}
      </Clickable>
      {showButton ? (
        <a
          className="p-[6px]"
          data-analytics-id="Search Bar - Go"
          href={getSuggestionHref('/cars', {})}
          data-native-payload={JSON.stringify({
            action: NavPayloadAction.ViewSrp
          })}
        >
          <SearchButton
            className={buttonSize === 'large' ? 'h-44 w-48' : undefined}
          >
            GO
          </SearchButton>
        </a>
      ) : null}
    </div>
  );
};

export default function SearchBarContainer(props: SearchBarProps) {
  const { inputValue, setInputValue, ...searchBarContextProps } = props;

  return (
    <SearchBarContextProvider {...searchBarContextProps}>
      <SuggestionsContextProvider>
        <InputContextProvider
          inputValue={inputValue}
          setInputValue={setInputValue}
        >
          <SearchBarData />
          <div
            data-package="search-bar"
            style={{ height: '100%', width: '100%' }}
          >
            {props.showModal ? <SearchBarModalButton /> : <SearchBar />}
          </div>
        </InputContextProvider>
      </SuggestionsContextProvider>
    </SearchBarContextProvider>
  );
}
